import React, { useState, useRef, useCallback, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";

// Mentions are stored inline in the post/comment body as @[Name](memberId).
// This is exactly what the flexiapi "Member Tagging Limit" gate counts, and the
// id lets us (later) resolve who was tagged for notifications.
export const MENTION_RE = /@\[([^\]]+)\]\((\d+)\)/g;

/** Render a plain-text body, turning @[Name](id) tokens into styled @Name chips. */
export function MentionText({ text, primary = "#16a34a", className = "" }) {
    if (!text) return null;
    const nodes = [];
    const re = new RegExp(MENTION_RE.source, "g");
    let last = 0;
    let key = 0;
    let m;
    while ((m = re.exec(text)) !== null) {
        if (m.index > last) nodes.push(text.slice(last, m.index));
        nodes.push(
            <span key={key++} className="font-semibold" style={{ color: primary }}>
                @{m[1]}
            </span>
        );
        last = m.index + m[0].length;
    }
    if (last < text.length) nodes.push(text.slice(last));
    return <span className={className}>{nodes}</span>;
}

/**
 * The active @query being typed, from the text before the caret.
 * The '@' must start the input or follow whitespace. Returns { query, start }
 * (start = index of the '@') or null when the caret isn't inside a mention.
 */
export function activeMentionQuery(text, caret) {
    const before = text.slice(0, caret);
    // `-` last in the class needs no escape (and CI lints the escape as an error).
    const m = before.match(/(^|\s)@([\w.-]*)$/);
    if (!m) return null;
    return { query: m[2], start: caret - m[2].length - 1 };
}

/**
 * Replace the active @query with a readable "@Name " label (what the composer
 * shows). The label is converted to @[Name](id) markup on submit — see
 * serializeMentions — so the composer stays clean while the stored body carries
 * the token the tag-limit gate counts and the feed renders as a chip.
 */
export function insertMention(text, caret, member) {
    const q = activeMentionQuery(text, caret);
    const start = q ? q.start : caret;
    const label = `@${member.name} `;
    return {
        value: text.slice(0, start) + label + text.slice(caret),
        caret: start + label.length,
    };
}

/** Convert picked "@Name" labels back into @[Name](id) markup for submission. */
export function serializeMentions(text, picked) {
    if (!text || !picked || picked.length === 0) return text;
    const seen = new Set();
    const uniq = [];
    for (const p of picked) {
        if (!seen.has(p.name)) {
            seen.add(p.name);
            uniq.push(p);
        }
    }
    // Longest names first so "@Ann Lee" is matched before "@Ann".
    uniq.sort((a, b) => b.name.length - a.name.length);
    let out = text;
    for (const p of uniq) {
        const esc = p.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        // Match "@Name" not already part of a longer word or an existing token.
        const re = new RegExp("@" + esc + "(?![\\w\\]])", "g");
        out = out.replace(re, `@[${p.name}](${p.id})`);
    }
    return out;
}

/**
 * @mention autocomplete for a controlled <input>/<textarea>.
 * Returns props to spread on the field (ref, onChange, onKeyDown, onBlur) plus a
 * `dropdown` node.
 *
 * The dropdown is PORTALED to <body> and positioned from the field's own
 * bounding rect, rather than being absolutely positioned inside a wrapper. An
 * absolutely-positioned menu depends on every ancestor behaving — a positioned
 * ancestor, an `overflow` that clips it, a transform that re-roots the
 * containing block, or a layout shift while it is open all move or hide it.
 * Measuring the field directly makes the position independent of all of that.
 * The wrapper no longer needs `position: relative` (harmless if it has one).
 */
export function useMentions({ value, setValue, members = [], primary = "#16a34a", dropUp = false }) {
    const ref = useRef(null);
    const picked = useRef([]); // {name, id} chosen from the dropdown (for serialize)
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [active, setActive] = useState(0);

    const filtered = open
        ? members
              .filter((m) => (m.name || "").toLowerCase().includes(query.toLowerCase()))
              .slice(0, 8)
        : [];

    const detect = useCallback((el) => {
        if (!el) return;
        const caret = el.selectionStart ?? el.value.length;
        const q = activeMentionQuery(el.value, caret);
        if (q) {
            setQuery(q.query);
            setActive(0);
            setOpen(true);
        } else {
            setOpen(false);
        }
    }, []);

    const onChange = (e) => {
        setValue(e.target.value);
        if (!e.target.value) picked.current = []; // reset once the field is cleared
        detect(e.target);
    };

    const pick = (member) => {
        const el = ref.current;
        const caret = el ? el.selectionStart ?? value.length : value.length;
        const next = insertMention(value, caret, member);
        picked.current.push({ name: member.name, id: member.id });
        setValue(next.value);
        setOpen(false);
        requestAnimationFrame(() => {
            if (el) {
                el.focus();
                el.setSelectionRange(next.caret, next.caret);
            }
        });
    };

    const onKeyDown = (e) => {
        if (!open || filtered.length === 0) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((a) => (a + 1) % filtered.length);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((a) => (a - 1 + filtered.length) % filtered.length);
        } else if (e.key === "Enter" || e.key === "Tab") {
            e.preventDefault();
            pick(filtered[active]);
        } else if (e.key === "Escape") {
            setOpen(false);
        }
    };

    // Field rect, in viewport coords, refreshed while the menu is open so the
    // list tracks the input through scrolling and layout changes instead of
    // being painted once and left behind.
    const [rect, setRect] = useState(null);
    const measure = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, bottom: r.bottom, left: r.left, width: r.width });
    }, []);

    useLayoutEffect(() => {
        if (!open) return undefined;
        measure();
        window.addEventListener("scroll", measure, true); // capture: inner scrollers too
        window.addEventListener("resize", measure);

        return () => {
            window.removeEventListener("scroll", measure, true);
            window.removeEventListener("resize", measure);
        };
    }, [open, measure]);

    // Re-measure as the list grows/shrinks — a taller menu changes where a
    // drop-up has to start.
    useEffect(() => {
        if (open) measure();
    }, [open, filtered.length, measure]);

    // Delay the close so a mouse click on an option lands before blur hides it.
    const onBlur = () => setTimeout(() => setOpen(false), 120);

    const MENU_MAX_H = 224; // matches max-h-56
    // Flip down when a drop-up would run off the top of the viewport, and vice
    // versa — the caller's `dropUp` is the preference, not a guarantee.
    const spaceAbove = rect ? rect.top : 0;
    const spaceBelow = rect ? window.innerHeight - rect.bottom : 0;
    const up = dropUp ? spaceAbove > 96 || spaceAbove >= spaceBelow : spaceBelow < 96 && spaceAbove > spaceBelow;

    // The field has to still be on screen. Tracking its rect keeps the menu with
    // the input while both are visible, but once the input scrolls out of the
    // viewport there is nothing to attach to — the menu was left sitting at the
    // edge of the screen, detached from the box it belongs to. Hide it instead;
    // the query and selection survive, so scrolling back brings it straight back.
    const fieldOnScreen = !!rect && rect.bottom > 0 && rect.top < window.innerHeight;

    const dropdown =
        open && filtered.length > 0 && rect && fieldOnScreen
            ? createPortal(
            <div
                style={{
                    position: "fixed",
                    left: rect.left,
                    width: rect.width,
                    // No Math.max clamp on `bottom`. Clamping to 8px pinned the
                    // menu just above the bottom of the window as soon as the
                    // input scrolled below the fold, so it hung there looking
                    // frozen. Let it follow the field honestly and go off-screen
                    // with it — fieldOnScreen above unmounts it before that.
                    ...(up
                        ? { bottom: window.innerHeight - rect.top + 4 }
                        : { top: rect.bottom + 4 }),
                    maxHeight: MENU_MAX_H,
                    zIndex: 10050, // above the modals this can be opened inside
                }}
                className="overflow-y-auto rounded-xl border border-gray-200 bg-surface shadow-lg"
            >
                {filtered.map((m, i) => (
                    <button
                        type="button"
                        key={m.id}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            pick(m);
                        }}
                        onMouseEnter={() => setActive(i)}
                        className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${
                            i === active ? "bg-gray-100" : "hover:bg-gray-50"
                        }`}
                    >
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-200 text-xs font-semibold text-gray-600">
                            {m.img ? (
                                <img src={m.img} alt="" className="h-full w-full object-cover" />
                            ) : (
                                (m.name || "?").charAt(0).toUpperCase()
                            )}
                        </span>
                        <span className="truncate font-medium text-gray-800">{m.name}</span>
                    </button>
                ))}
            </div>,
            document.body,
        )
            : null;

    const serialize = (text) => serializeMentions(text, picked.current);

    return { ref, onChange, onKeyDown, onBlur, dropdown, serialize };
}
